import React from "react";
import { isAiCapabilityIdOff, NAVIGATION_CAPABILITY_ID } from "./ai-capability-preferences.store";
import { Messages } from "../../core/api";
import { auth } from "../../core/auth";
import {
  AiCapability,
  AiCapabilityEffect,
  AiCapabilityParamOption,
  AiCapabilityRunner,
  AiCapabilitySpec,
} from "./ai-capability.types";

/**
 * The one place the omnibar looks to find out what the app can do.
 *
 * Nothing here knows about patients, visits or navigation - components put themselves in,
 * from their own file, and take themselves out when they unmount. Two levels on purpose:
 *
 *   declare(spec)          the capability exists in this build, but cannot run yet
 *   provide(id, run)       its owner is mounted, so it can run now
 *   register(spec, run)    both at once, for things that are always reachable
 *
 * The split is what lets the model plan a step it cannot take yet: the patient modal's
 * "new visit" side bar is declared at load with `requires: ["patient.openModal"]`, and only
 * becomes available once a patient modal is actually open.
 */

type RegistryEntry = {
  /** Null until the owning component declares itself; an entry can be created earlier than that
   *  by another component contributing an option to it. */
  spec: AiCapabilitySpec | null;
  runners: AiCapabilityRunner[];
  /** Options contributed by other components, keyed by param name. */
  contributedOptions: Map<string, Map<string, AiCapabilityParamOption>>;
};

const entries = new Map<string, RegistryEntry>();
const listeners = new Set<() => void>();

function getOrCreateEntry(id: string): RegistryEntry {
  const existing = entries.get(id);

  if (existing !== undefined) {
    return existing;
  }

  const created: RegistryEntry = {
    spec: null,
    runners: [],
    contributedOptions: new Map(),
  };

  entries.set(id, created);

  return created;
}

function notify() {
  listeners.forEach((listener) => listener());
}

export function subscribeToAiCapabilities(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

/**
 * Whether this user holds a permission key of the form the `view-permission` directive accepts:
 * absent means "no permission needed", and "a|b" means either will do.
 *
 * Exported because a capability that generates its own enum options - the profile sections, the
 * sidebar destinations - has to filter those options the same way, so the model is only ever shown
 * the parts of the app this user can actually reach.
 */
export function isPermittedForAi(permissionKey: string | undefined): boolean {
  if (permissionKey === undefined || permissionKey === "") {
    return true;
  }

  try {
    return permissionKey
      .split("|")
      .some((key) => auth.isAllowedTo(key as Messages["AgencyMemberPermission"]));
  } catch {
    return false;
  }
}

const ALLOWED_EFFECTS: AiCapabilityEffect[] = ["navigate", "prefill"];

/**
 * The gate for the rule that the omnibar only navigates and prefills.
 *
 * TypeScript already rejects a bad `effect` in .ts/.tsx callers, but the AngularJS half of the app
 * is plain JS, so the check has to exist at runtime as well. A capability that does not classify
 * itself never enters the registry, which means the model is never told about it.
 */
function hasAllowedEffect(spec: AiCapabilitySpec): boolean {
  if (ALLOWED_EFFECTS.includes(spec.effect)) {
    return true;
  }

  console.error(
    `[ai-omnibar] refusing capability "${spec.id}": effect must be one of ${ALLOWED_EFFECTS.join(
      " | "
    )}, got ${JSON.stringify(spec.effect)}. The omnibar may only navigate and prefill.`
  );

  return false;
}

/**
 * States that a capability exists. Call it at module scope, next to the component that owns
 * it, so the description lives with the code that has to honour it.
 */
export function declareAiCapability(spec: AiCapabilitySpec): void {
  if (!hasAllowedEffect(spec)) {
    return;
  }

  const entry = getOrCreateEntry(spec.id);
  entry.spec = spec;
  notify();
}

/**
 * Marks a declared capability as runnable, for as long as the returned function is uncalled.
 */
export function provideAiCapability(id: string, run: AiCapabilityRunner): () => void {
  const declared = entries.get(id);

  if (declared === undefined || declared.spec === null) {
    console.error(
      `[ai-omnibar] "${id}" was never declared (or was refused), so it cannot be provided.`
    );

    return () => undefined;
  }

  const entry = declared;
  entry.runners.push(run);
  notify();

  let released = false;

  return () => {
    if (released) {
      return;
    }

    released = true;
    entry.runners = entry.runners.filter((candidate) => candidate !== run);
    notify();
  };
}

/**
 * declare + provide, for capabilities that work anywhere in the app.
 */
export function registerAiCapability(spec: AiCapabilitySpec, run: AiCapabilityRunner): () => void {
  declareAiCapability(spec);

  return provideAiCapability(spec.id, run);
}

/**
 * Adds one choice to an `enum` param of another capability. The sidebar's navigation items
 * use this to describe their own destination without the omnibar holding a list of pages.
 */
export function contributeAiCapabilityParamOption(
  capabilityId: string,
  paramName: string,
  option: AiCapabilityParamOption
): () => void {
  const entry = getOrCreateEntry(capabilityId);
  const forParam = entry.contributedOptions.get(paramName) ?? new Map();

  forParam.set(option.value, option);
  entry.contributedOptions.set(paramName, forParam);
  notify();

  let released = false;

  return () => {
    if (released) {
      return;
    }

    released = true;
    const current = entry.contributedOptions.get(paramName);
    current?.delete(option.value);
    notify();
  };
}

/**
 * Where the user is, so the model can tell "the patient you have open" from "patient 2".
 * Components that own an overlay describe themselves here and clear it when they close.
 */

let currentPage: string | null = null;
const contextEntities = new Map<string, string>();

export function setAiCurrentPage(page: string | null): void {
  currentPage = page;
}

export function getAiCurrentPage(): string | null {
  return currentPage;
}

export function setAiContextEntity(key: string, description: string): () => void {
  contextEntities.set(key, description);
  notify();

  return () => {
    if (contextEntities.get(key) === description) {
      contextEntities.delete(key);
      notify();
    }
  };
}

export function getAiContextEntities(): string[] {
  return [...contextEntities.values()];
}

export function getAiCapability(id: string): AiCapability | undefined {
  const entry = entries.get(id);

  if (entry === undefined || entry.spec === null) {
    return undefined;
  }

  return { ...entry.spec, run: entry.runners.at(-1) };
}

export function getAiCapabilities(): AiCapability[] {
  return declaredEntries().map((entry) => ({ ...entry.spec, run: entry.runners.at(-1) }));
}

export type AiCapabilitySetting = {
  id: string;
  title: string;
  isEnabled: boolean;
  /** The way in this one sits behind, when it needs one first. */
  parentId: string | null;
};

/**
 * Every capability this member is allowed to use, switched on or off.
 *
 * Not the manifest: that one has the switched-off ones already removed, which is
 * the whole point of it and exactly wrong for a panel whose job is to show what
 * you have turned off. Permission filtering is shared, though - a capability the
 * role cannot use is not a choice to offer.
 */
export function getAiCapabilitySettings(): AiCapabilitySetting[] {
  const ways = declaredEntries()
    .filter((entry) => isPermittedForAi(entry.spec.permissionKey))
    // Only the ways in. The filters and forms behind them follow, so listing those too would be a
    // page of switches that mostly cannot be set on their own.
    .filter((entry) => entry.spec.effect === "navigate");
  const ids = new Set(ways.map((entry) => entry.spec.id));

  return ways
    .map((entry) => {
      // A way in that needs another way in first - a patient's section behind the patient's
      // profile - names its parent, so the panel can nest it. Only a parent that is itself listed
      // counts; anything else has no row to sit under.
      const parentId = findHostsOfScope(entry.spec.scope).find((host) => ids.has(host)) ?? null;

      return {
        id: entry.spec.id,
        title: entry.spec.title,
        isEnabled: !isAiCapabilityIdOff(entry.spec.id),
        parentId: parentId === entry.spec.id ? null : parentId,
      };
    })
    .sort((a, b) => a.title.localeCompare(b.title));
}

/**
 * Which capabilities have to run first for something in `scope` to become reachable.
 *
 * This is the whole point of the scope indirection: a child publishes the space it belongs to, a
 * host publishes the spaces it opens, and the connection between them is worked out here rather
 * than written down by either side. Neither knows the other's capabilities, and a scope with two
 * hosts simply yields two ways in.
 */
function findHostsOfScope(scope: string | undefined): string[] {
  if (scope === undefined) {
    return [];
  }

  return declaredEntries()
    .filter((entry) => (entry.spec.opens ?? []).includes(scope))
    .map((entry) => entry.spec.id);
}

/**
 * Entries whose owner has declared itself. The rest are placeholders holding options contributed
 * to a capability that has not introduced itself yet, and have nothing to tell the model.
 */
function declaredEntries(): (RegistryEntry & { spec: AiCapabilitySpec })[] {
  return [...entries.values()].filter(
    (entry): entry is RegistryEntry & { spec: AiCapabilitySpec } => entry.spec !== null
  );
}

export function isAiCapabilityAvailable(id: string): boolean {
  return (entries.get(id)?.runners.length ?? 0) > 0;
}

/**
 * Waits for a capability to become runnable, which is how a plan gets to open the patient
 * modal and then reach into it.
 */
export function waitForAiCapability(id: string, timeoutMs: number): Promise<boolean> {
  if (isAiCapabilityAvailable(id)) {
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    const timeout = window.setTimeout(() => {
      unsubscribe();
      resolve(false);
    }, timeoutMs);

    const unsubscribe = subscribeToAiCapabilities(() => {
      if (isAiCapabilityAvailable(id)) {
        window.clearTimeout(timeout);
        unsubscribe();
        resolve(true);
      }
    });
  });
}

/** How often the option lists are re-read while waiting for a page's fetch to land. */
const OPTIONS_POLL_MS = 100;

/**
 * Waits for the option lists a step is about to read to have something in them.
 *
 * A page provides its capability when it mounts, but builds its dropdowns from rows it is still
 * fetching. A step arriving right behind a navigation therefore finds empty lists and concludes the
 * page cannot filter by what it was asked for - which is indistinguishable, to the person reading
 * the reply, from the page really not offering it. The lists are read through thunks reaching into
 * the page and nothing announces when they fill, so this polls rather than subscribes.
 *
 * It resolves either way. An empty list once the wait is over is a real answer.
 */
export function waitForAiCapabilityParamOptions(
  capabilityId: string,
  paramNames: string[],
  timeoutMs: number
): Promise<void> {
  const spec = entries.get(capabilityId)?.spec ?? null;

  if (spec === null) {
    return Promise.resolve();
  }

  // Only params with a list to fill. A date or a free-text search never has options, and waiting
  // for one would be waiting out the whole timeout for nothing.
  const wanted = paramNames.filter((name) => {
    const param = (spec.params ?? []).find((candidate) => candidate.name === name);

    return param !== undefined && (param.type === "enum" || param.options !== undefined);
  });

  const isFilled = () =>
    wanted.every((name) => getAiCapabilityParamOptions(capabilityId, name).length > 0);

  if (wanted.length === 0 || isFilled()) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    let waited = 0;

    const poll = window.setInterval(() => {
      waited += OPTIONS_POLL_MS;

      if (isFilled() || waited >= timeoutMs) {
        window.clearInterval(poll);
        resolve();
      }
    }, OPTIONS_POLL_MS);
  });
}

/**
 * The manifest sent to the server. Everything the model is told about the app is built from
 * what components put in the registry - there is no second list to keep in sync.
 */
/**
 * The options a param will accept right now: what its owner declared, plus what other mounted
 * components have contributed.
 *
 * Evaluated on each call rather than at declaration time. A declared list may be a thunk that
 * filters by permission - during boot there is no permission set to filter against - and a
 * contributed list only exists while the component that owns it is mounted.
 */
export function getAiCapabilityParamOptions(
  capabilityId: string,
  paramName: string
): AiCapabilityParamOption[] {
  const entry = entries.get(capabilityId);

  if (entry === undefined || entry.spec === null) {
    return [];
  }

  const param = (entry.spec.params ?? []).find((candidate) => candidate.name === paramName);

  if (param === undefined) {
    return [];
  }

  const contributed = [...(entry.contributedOptions.get(paramName)?.values() ?? [])];
  const declared = typeof param.options === "function" ? param.options() : param.options ?? [];

  // A param can have both: declared options give the vocabulary, a mounted component contributes
  // what is really there. Same value from both sides is one option, not two.
  return [
    ...new Map([...declared, ...contributed].map((option) => [option.value, option])).values(),
  ];
}

/**
 * What the app tells the planner it can do.
 *
 * No option values go to the planner - only how many a field has. The lists are agency data, they
 * were a third of the prompt, they were re-sent whether or not the request was about them, and for
 * anything page-specific they do not exist until that page is open. The planner works in the user's
 * words and names the field it thinks they belong to; `searchStepValues` in useAiOmnibar looks them
 * up afterwards, in the lists the mounted components really have.
 */
/**
 * Whether the AI may use this capability, given what the person has switched off.
 *
 * Only the ways in are switched: a page, a profile, a report. Everything you can do once you are
 * there - filtering the list, filling in a form, laying out a report - follows the way in, because
 * that is how it reads to the person switching. "The AI may not open claims" should mean it cannot
 * filter them either, and offering both switches invites a state where it can filter a page it
 * cannot reach.
 *
 * The link is not a naming convention: a capability declares the space it lives in, hosts declare
 * the spaces they open, and `findHostsOfScope` matches them. Anything with no host of its own -
 * reachable from anywhere, like creating a note - follows the general navigation switch.
 */
function isAllowedByPreferences(spec: AiCapabilitySpec, seen: Set<string> = new Set()): boolean {
  // A way in can sit behind another - a patient's notes behind the patient's profile - so the
  // question is asked of the parent too. The guard is for a declaration that names itself, however
  // indirectly, which would otherwise hang the manifest rather than answer it.
  if (seen.has(spec.id)) {
    return true;
  }

  seen.add(spec.id);

  if (spec.effect === "navigate" && isAiCapabilityIdOff(spec.id)) {
    return false;
  }

  const hosts = findHostsOfScope(spec.scope);

  if (hosts.length === 0) {
    return spec.effect === "navigate" || !isAiCapabilityIdOff(NAVIGATION_CAPABILITY_ID);
  }

  return hosts.some((host) => {
    // A host that has been provided but never declared has no spec to ask, so its own switch is
    // the whole answer.
    const hostSpec = entries.get(host)?.spec ?? null;

    return hostSpec === null ? !isAiCapabilityIdOff(host) : isAllowedByPreferences(hostSpec, seen);
  });
}

export function getAiCapabilityManifest(): Messages["AiOmnibarCapability"][] {
  return (
    declaredEntries()
      .filter((entry) => isPermittedForAi(entry.spec.permissionKey))
      // Switched off in AI settings. Filtered here rather than at the call site so there is one
      // answer to "what may the AI do" - the panel that lists them reads the same registry.
      .filter((entry) => isAllowedByPreferences(entry.spec))
      .map((entry) => ({
        id: entry.spec.id,
        title: entry.spec.title,
        description: entry.spec.description,
        location: entry.spec.location,
        effect: entry.spec.effect,
        isAvailableNow: entry.runners.length > 0,
        reachableAfter: findHostsOfScope(entry.spec.scope),
        examples: entry.spec.examples ?? [],
        isLastResort: entry.spec.isLastResort ?? false,
        params: (entry.spec.params ?? []).map((param) => {
          const paramOptions = getAiCapabilityParamOptions(entry.spec.id, param.name);

          return {
            name: param.name,
            type: param.type,
            required: param.required ?? false,
            description: param.description,
            options: null,
            // The count is all planning gets: a closed set has to read as closed, or the model fills
            // a fixed field with free prose.
            optionCount: paramOptions.length === 0 ? null : paramOptions.length,
          };
        }),
      }))
  );
}

/**
 * Provides an already-declared capability for as long as this component is mounted.
 *
 * This is the hook to reach for. Pair it with a `declareAiCapability` call at module scope in the
 * same file: the declaration runs when the module is imported, so the model is told the capability
 * exists even while nothing is rendered, and can plan its way to it through `requires`. Only its
 * availability follows the component.
 *
 *   // module scope - the model knows about this from app boot
 *   declareAiCapability({ id: "patient.taskActivity.open", scope: "patient-profile", ... });
 *
 *   // in the component - it can actually run now
 *   useProvideAiCapability("patient.taskActivity.open", () => { ... });
 *
 * Pass `{ enabled: false }` while the component is mounted but not yet able to perform the action -
 * data still loading, for instance. Availability has to mean the capability would really work, or
 * the model plans a step that then fails.
 */
export function useProvideAiCapability(
  id: string,
  run: AiCapabilityRunner,
  options?: { enabled?: boolean }
): void {
  const runRef = React.useRef(run);
  runRef.current = run;

  const isEnabled = options?.enabled ?? true;

  React.useEffect(() => {
    if (!isEnabled) {
      return;
    }

    return provideAiCapability(id, (args, context) => runRef.current(args, context));
  }, [id, isEnabled]);
}

/**
 * Declares *and* provides in one step, both scoped to this component being mounted.
 *
 * Only use this when the model has no reason to know the capability exists until the user is
 * already looking at the component - "sort this table", "expand all rows on this page". Because the
 * declaration happens on mount, the capability is absent from the manifest while the component is
 * unmounted, so the model cannot plan a route to it. Anything the AI should be able to *get* to
 * wants `declareAiCapability` at module scope plus `useProvideAiCapability` instead.
 */
export function useAiCapability(spec: AiCapabilitySpec, run: AiCapabilityRunner): void {
  const runRef = React.useRef(run);
  runRef.current = run;

  const specKey = JSON.stringify(spec);

  React.useEffect(() => {
    return registerAiCapability(JSON.parse(specKey) as AiCapabilitySpec, (args, context) =>
      runRef.current(args, context)
    );
  }, [specKey]);
}

/**
 * Re-renders on any registry change. For the omnibar itself, and for anything that wants to
 * show what the AI can currently do.
 */
export function useAiCapabilities(): AiCapability[] {
  const [capabilities, setCapabilities] = React.useState(getAiCapabilities);

  React.useEffect(() => {
    setCapabilities(getAiCapabilities());

    return subscribeToAiCapabilities(() => setCapabilities(getAiCapabilities()));
  }, []);

  return capabilities;
}
